import { useState } from 'react'
import { Button } from '#app/components/ui/button'
import { Icon } from '#app/components/ui/icon'
import { InputText } from '#app/components/ui/input-text'

interface PersonalData {
	nombreApellido: string
	documento: string
	estadoCivil: string
	actividad: string
	localidad: string
	email: string
	telefono: string
}

interface ManifestacionBienesPersonalScreenProps {
	onNext: (data: PersonalData) => void
	onBack: () => void
	data?: Partial<PersonalData>
}

const ESTADO_CIVIL_OPTIONS = [
	{ value: 'soltero', label: 'Soltero/a' },
	{ value: 'casado', label: 'Casado/a' },
	{ value: 'divorciado', label: 'Divorciado/a' },
	{ value: 'viudo', label: 'Viudo/a' },
	{ value: 'union-convivencial', label: 'Unión Convivencial' },
]

export function ManifestacionBienesPersonalScreen({
	onNext,
	onBack,
	data,
}: ManifestacionBienesPersonalScreenProps) {
	const [formData, setFormData] = useState<PersonalData>({
		nombreApellido: data?.nombreApellido || '',
		documento: data?.documento || '',
		estadoCivil: data?.estadoCivil || '',
		actividad: data?.actividad || '',
		localidad: data?.localidad || '',
		email: data?.email || '',
		telefono: data?.telefono || '',
	})

	const [errors, setErrors] = useState<Record<string, string>>({})

	const validateEmail = (email: string) => {
		return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
	}

	const handleSubmit = () => {
		const newErrors: Record<string, string> = {}

		if (!formData.nombreApellido.trim()) {
			newErrors.nombreApellido = 'Ingresa el nombre y apellido'
		}
		if (!formData.documento.trim()) {
			newErrors.documento = 'Ingresa el documento'
		}
		if (!formData.estadoCivil) {
			newErrors.estadoCivil = 'Selecciona el estado civil'
		}
		if (!formData.actividad.trim()) {
			newErrors.actividad = 'Ingresa la actividad o profesión'
		}
		if (!formData.localidad.trim()) {
			newErrors.localidad = 'Ingresa la localidad'
		}
		if (!formData.email.trim()) {
			newErrors.email = 'Ingresa el email'
		} else if (!validateEmail(formData.email)) {
			newErrors.email = 'Email inválido'
		}
		if (!formData.telefono.trim()) {
			newErrors.telefono = 'Ingresa el teléfono'
		}

		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors)
			return
		}

		onNext(formData)
	}

	const isFormValid =
		formData.nombreApellido.trim() &&
		formData.documento.trim() &&
		formData.estadoCivil &&
		formData.actividad.trim() &&
		formData.localidad.trim() &&
		formData.email.trim() &&
		formData.telefono.trim()

	return (
		<div className="flex h-full w-full flex-col">
			<div className="flex-1 overflow-y-auto">
				<div className="flex w-full flex-col items-center gap-6">
					<h2 className="font-parkinsans text-center text-2xl leading-8 font-medium text-blue-800">
						Datos del Manifestante
					</h2>

					<div className="flex w-full flex-col gap-4">
						<InputText
							label="Nombre y Apellido"
							labelPosition="internal"
							size="l"
							placeholder="Nombre completo"
							value={formData.nombreApellido}
							onChange={(e) => {
								setFormData({ ...formData, nombreApellido: e.target.value })
								setErrors({ ...errors, nombreApellido: '' })
							}}
							errorText={errors.nombreApellido}
							required
						/>

						<div className="grid grid-cols-2 gap-4">
							<InputText
								label="Documento (Tipo y N°)"
								labelPosition="internal"
								size="l"
								placeholder="DNI 12345678"
								value={formData.documento}
								onChange={(e) => {
									setFormData({ ...formData, documento: e.target.value })
									setErrors({ ...errors, documento: '' })
								}}
								errorText={errors.documento}
								required
							/>
							<div className="flex flex-col gap-1">
								<label className="text-sm font-medium text-neutral-700">
									Estado Civil <span className="text-red-500">*</span>
								</label>
								<select
									className={`w-full rounded-xl border px-4 py-3 text-base ${
										errors.estadoCivil ? 'border-red-500' : 'border-neutral-300'
									}`}
									value={formData.estadoCivil}
									onChange={(e) => {
										setFormData({ ...formData, estadoCivil: e.target.value })
										setErrors({ ...errors, estadoCivil: '' })
									}}
								>
									<option value="">Selecciona</option>
									{ESTADO_CIVIL_OPTIONS.map((opt) => (
										<option key={opt.value} value={opt.value}>
											{opt.label}
										</option>
									))}
								</select>
								{errors.estadoCivil && (
									<span className="text-sm text-red-500">
										{errors.estadoCivil}
									</span>
								)}
							</div>
						</div>

						<InputText
							label="Actividad / Profesión"
							labelPosition="internal"
							size="l"
							placeholder="Tu ocupación"
							value={formData.actividad}
							onChange={(e) => {
								setFormData({ ...formData, actividad: e.target.value })
								setErrors({ ...errors, actividad: '' })
							}}
							errorText={errors.actividad}
							required
						/>

						<InputText
							label="Localidad"
							labelPosition="internal"
							size="l"
							placeholder="Ciudad o localidad"
							value={formData.localidad}
							onChange={(e) => {
								setFormData({ ...formData, localidad: e.target.value })
								setErrors({ ...errors, localidad: '' })
							}}
							errorText={errors.localidad}
							required
						/>

						<InputText
							label="Email"
							labelPosition="internal"
							size="l"
							placeholder="correo@ejemplo.com"
							type="email"
							value={formData.email}
							onChange={(e) => {
								setFormData({ ...formData, email: e.target.value })
								setErrors({ ...errors, email: '' })
							}}
							errorText={errors.email}
							required
						/>

						<InputText
							label="Teléfono"
							labelPosition="internal"
							size="l"
							placeholder="Teléfono de contacto"
							value={formData.telefono}
							onChange={(e) => {
								setFormData({ ...formData, telefono: e.target.value })
								setErrors({ ...errors, telefono: '' })
							}}
							errorText={errors.telefono}
							required
						/>
					</div>
				</div>
			</div>

			<div className="flex w-full shrink-0 flex-col items-center justify-start gap-2 bg-white pt-4 md:flex-row md:justify-between md:bg-transparent md:pt-8">
				<Button
					variant="primary"
					appearance="ghost"
					size="l"
					onClick={onBack}
					className="order-2 w-full gap-2 rounded-xl px-4 py-3 text-sm leading-4 md:order-1 md:w-auto md:px-6 md:py-4 md:text-base md:leading-5"
				>
					<Icon name="arrow-left" className="size-4" />
					Atrás
				</Button>
				<Button
					variant="secondary"
					appearance="solid"
					size="l"
					onClick={handleSubmit}
					disabled={!isFormValid}
					className="order-1 w-full md:order-2 md:w-auto"
				>
					Siguiente
					<Icon name="arrow-right" className="size-3.5" />
				</Button>
			</div>
		</div>
	)
}
